/**
 * How tall the screen actually is, right now.
 *
 * `100dvh` is supposed to answer this and on iOS Safari it does not reliably.
 * Safari's bottom toolbar is an overlay that grows and shrinks as you scroll,
 * and `dvh` both lags that animation and, in several situations, resolves
 * toward the *large* viewport — the one measured with the toolbar hidden. A
 * layout sized that way puts its bottom row underneath the toolbar, which is
 * exactly where the navigation bar was ending up.
 *
 * `visualViewport` reports the visible region directly, including the toolbar
 * and the on-screen keyboard, so it is used as the source of truth and `dvh`
 * stays only as the fallback for browsers without it.
 */

/**
 * Below this fraction of the tallest viewport we have seen, assume the
 * on-screen keyboard is open rather than a toolbar having appeared. Toolbars
 * take 10–15% of the screen; keyboards take 35–50%, so the gap is wide and the
 * threshold does not need to be precise.
 */
const KEYBOARD_RATIO = 0.75;

export function trackViewportHeight(): () => void {
  const root = document.documentElement;
  const viewport = window.visualViewport;

  if (!viewport) {
    // Nothing to improve on: the CSS fallback already handles this case.
    return () => undefined;
  }

  let tallest = viewport.height;

  const apply = () => {
    /*
     * Pinch-zoom shrinks the visual viewport without changing the layout. Using
     * it here would shrink the whole app as the user zoomed in, so the reading
     * is only trusted at natural scale.
     */
    if (viewport.scale > 1.01) return;

    const height = viewport.height;
    tallest = Math.max(tallest, height);

    root.style.setProperty("--fc-vh", `${String(Math.round(height))}px`);

    const keyboardOpen = height < tallest * KEYBOARD_RATIO;
    if (keyboardOpen) root.dataset.keyboard = "open";
    else delete root.dataset.keyboard;
  };

  apply();

  // `scroll` matters as much as `resize`: on iOS the toolbar collapses during a
  // scroll and the height changes without a resize event firing.
  viewport.addEventListener("resize", apply);
  viewport.addEventListener("scroll", apply);

  /*
   * `window.resize` as well, because the two do not fire in the same
   * situations and a missed update is worse than not measuring at all — the
   * layout would be pinned to a height the screen no longer has. Whichever
   * fires first wins; `apply` is idempotent.
   */
  window.addEventListener("resize", apply);

  // A rotation changes the tallest possible viewport, so the baseline has to go
  // with it or the keyboard heuristic misfires for the rest of the session.
  const onOrientation = () => {
    tallest = viewport.height;
    apply();
  };
  window.addEventListener("orientationchange", onOrientation);

  // Restoring from the back/forward cache does not re-run module setup, and
  // Safari commonly restores at a different toolbar state than it left.
  window.addEventListener("pageshow", apply);

  return () => {
    viewport.removeEventListener("resize", apply);
    viewport.removeEventListener("scroll", apply);
    window.removeEventListener("resize", apply);
    window.removeEventListener("orientationchange", onOrientation);
    window.removeEventListener("pageshow", apply);
    root.style.removeProperty("--fc-vh");
    delete root.dataset.keyboard;
  };
}
